import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { getCurrentDate } from '@pesto/utils';
import { z } from 'zod';

import type { CreatePrimeRateInput } from '../../../__generated__/graphql/api';
import type { ModalProps } from '../../types/modal.types';

import { ListPrimeRatesDocument, useCreatePrimeRateMutation } from '../../../__generated__/graphql/api';
import { createFormComponent } from '../../utils/create-form-component';

const createPrimeRateSchema = z.object({
  effectiveDate: z.string().min(1, 'Effective date is required'),
  rate: z
    .string()
    .min(1, 'Rate is required')
    .regex(/^\d+(\.\d+)?$/, 'Rate must be a valid number')
    .refine((val) => parseFloat(val) >= 0 && parseFloat(val) <= 100, 'Rate must be between 0 and 100'),
  source: z.string().optional(),
});

type CreatePrimeRateFormData = z.infer<typeof createPrimeRateSchema>;

const formConfig: SmartFormConfig<CreatePrimeRateFormData> = {
  fields: [
    {
      name: 'effectiveDate',
      label: 'Effective Date',
      type: 'date',
      defaultValue: getCurrentDate(),
    },
    {
      name: 'rate',
      label: 'Rate (%)',
      type: 'text',
      defaultValue: '',
      placeholder: 'e.g., 8.50',
    },
    {
      name: 'source',
      label: 'Source',
      type: 'text',
      defaultValue: '',
      placeholder: 'e.g., WSJ',
    },
  ],
  schema: createPrimeRateSchema,
  defaultValues: {
    effectiveDate: getCurrentDate(),
    rate: '',
    source: '',
  },
};

export const CreatePrimeRateComponent = ({ api }: ModalProps<undefined, true>) => {
  const { handleMutation } = useMutationWithToast();

  const [createPrimeRate, { loading }] = useCreatePrimeRateMutation({
    refetchQueries: [{ query: ListPrimeRatesDocument }],
  });

  const handleSubmit = (data: CreatePrimeRateFormData) => {
    const variables: CreatePrimeRateInput = {
      effectiveDate: data.effectiveDate,
      rate: parseFloat(data.rate),
      source: data.source?.trim() || undefined,
    };

    void handleMutation({
      mutation: () => createPrimeRate({ variables: { input: variables } }),
      extractSuccess: (result) => !!result?.data?.createPrimeRate,
      debugLabel: 'create-prime-rate',
      toast: {
        success: 'Prime rate created successfully',
        error: 'Failed to create prime rate',
        loading: 'Creating prime rate...',
      },
      onSuccess: () => {
        api.close(true);
      },
    });
  };

  return createFormComponent({
    config: formConfig,
    handleSubmit,
    loading,
  });
};
